import styles from './paymentTypeForm.module.css'
import { useEffect, useMemo, useState } from "react"
import { DataTypeForm, Labels } from "../../../../../features/data-types"
import { PanelWrapper, Data, FieldType, FieldValue, FormField, option, useGetCrumbs, O, BooleanField, NumberField, PriceField, ModalId, useUIActions, Validatable } from "../../../../../features/ui"
import { PaymentCategory, PaymentType } from "../../../state/types"
import { useActions } from "./actions"
import { AdjustmentType } from "../../../../operations"
import { useNavigate } from "react-router-dom"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { define } from '../../../../../utils/typeUtils'
import { TableName } from '../../../../../tables'
import { CashFundItemsTable } from '../../cash-fund/cash-fund-items-table/cashFundItemsTable'
import { CashFundItemFormModal } from '../../cash-fund/cash-fund-item-form-modal/cashFundItemFormModal'
import { useCashFundLoaders } from '../../../loaders/cashFundLoaders'
import { toList } from '../../../../../utils/listUtils'

export const PaymentTypeForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const paymentType = dataType as O<PaymentType>
    const createMode = !paymentType

    const [category, setCategory] = useState<O<PaymentCategory>>(paymentType?.category)
    const defaultType = useMemo(() => paymentType && paymentType?.yield < 0 ? AdjustmentType.DISCOUNT : AdjustmentType.SURCHARGE, [paymentType?.yield])
    const [adjustmentType, setAdjustmentType] = useState<O<AdjustmentType>>(defaultType)
    const [adjustmentYield, setAdjustmentYield] = useState<number>(paymentType?.yield || 0)
    const [financialCost, setFinancialCost] = useState<O<number>>(paymentType?.financialCost || 0)
    const [immediateAccreditation, setImmediateAccreditation] = useState<boolean>(paymentType ? paymentType?.accreditationDays === 0 : true)
    const [accreditationDays, setAccreditationDays] = useState<O<number>>(paymentType?.accreditationDays || 0)
    const defaultCashFundsData = useMemo(() => paymentType?.cashFundIds.map(cashFundId => ({ cashFundId })), [paymentType?.cashFundIds])
    const [cashFundsData, setCashFundsData] = useState<Data[]>(defaultCashFundsData || [])
    const [cashFundsValidation, setCashFundsValidation] = useState<O<string>>()
    const [roundingEnabled, setRoundingEnabled] = useState<boolean>(paymentType?.roundingEnabled || false)
    const [maxRoundingAmount, setMaxRoundingAmount] = useState<O<number>>(paymentType?.maxRoundingAmount)
    const [enabled, setEnabled] = useState<boolean>(!paymentType?.disabled)

    const { getRoutePath } = useRoutesHooks()
    const navigate = useNavigate()

    const cashFundLoaders = useCashFundLoaders()

    const { submitPaymentType } = useActions()
    const { openModal } = useUIActions()
    
    useEffect(() => {
        if (paymentType && paymentType.category === PaymentCategory.CREDIT_ACCOUNT) {
            navigate(getRoutePath(RoutePath.PAYMENT_TYPES, paymentType.id))
        }
    }, [paymentType])

    const paymentTypeLabel = paymentType ? `: ${paymentType.name}` : ''
    const title = `${createMode ? 'Crear' : 'Editar'} Medio de Pago${paymentTypeLabel}`

    const crumbs = useGetCrumbs(
        'Medios de Pago',
        RoutePath.PAYMENT_TYPES,
        paymentType?.name,
        paymentType?.id,
        createMode ? 'Crear' : 'Editar'
    )

    const typeOptions = useMemo(() => Object.values(AdjustmentType).map(value => option(value, Labels.adjustmentTypeLabel(value))), [])
    const allCashFunds = useMemo(() => cashFundLoaders().findAllCashFunds(true), [])
    const cashFunds = useMemo(() => allCashFunds.filter(cashFund =>
        !cashFundsData.map(cashFundData => cashFundData.cashFundId).includes(cashFund.id)
    ), [allCashFunds, cashFundsData])

    const onChangeCategory = (value?: FieldValue) => {
        const newCategory = value as O<PaymentCategory>
        setCategory(newCategory)
        if (newCategory === PaymentCategory.CHECK) {
            setFinancialCost(0)
            setImmediateAccreditation(true)
            setAccreditationDays(0)
            setCashFundsData([])
        }
    }

    const onChangeAdjustmentType = (value?: FieldValue) => {
        const newType = value as O<AdjustmentType>
        setAdjustmentType(newType)
        const yieldAbsoluteValue = Math.abs(adjustmentYield)
        setAdjustmentYield(newType === AdjustmentType.DISCOUNT ? -yieldAbsoluteValue : yieldAbsoluteValue)
    }

    const onChangeYield = (value?: FieldValue) => {
        const newYield = (value || 0) as number
        setAdjustmentYield(adjustmentType === AdjustmentType.DISCOUNT && newYield !== 0 ? -newYield : newYield)
    }

    const onChangeFinancialCost = (value?: FieldValue) => setFinancialCost(value as O<number>)

    const onChangeImmediateAccreditation = (value?: boolean) => {
        const newValue = !!value as boolean
        setImmediateAccreditation(newValue)
        newValue && setAccreditationDays(0)
    }

    const onChangeAccreditationDays = (value?: FieldValue) => setAccreditationDays(value as O<number>)
    
    const onChangeRoundingEnabled = (value?: boolean) => {
        const newValue = !!value
        setRoundingEnabled(newValue)
        if (!newValue) setMaxRoundingAmount(undefined)
    }

    const onAddCashFund = () => {
        setCashFundsValidation(undefined)
        openModal(ModalId.CASH_FUND_ITEM_FORM)
    }
    
    const onRemoveCashFund = (cashFundId: string) => {
        const newCashFundsData = cashFundsData.filter(cashFundData => cashFundData.cashFundId !== cashFundId)
        setCashFundsData(newCashFundsData)
    }
    
    const fields: FormField[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'category',
            type: FieldType.SELECT,
            label: 'Tipo',
            options: Object.values(PaymentCategory).map(category => option(category, Labels.paymentCategoryLabel(category))),
            onChange: onChangeCategory
        },
        {
            name: 'enabled',
            type: FieldType.BOOLEAN,
            label: 'Habilitado',
            hint: 'Si no está habilitado, no se ofrece para elegir al vender, cobrar o pagar. Se puede volver a habilitar cuando se quiera.',
            render: () => (
                <BooleanField
                    id="enabled"
                    value={enabled}
                    label="Ofrecer este medio de pago"
                    optional
                    onChange={value => setEnabled(!!value)}
                />
            )
        },
        {
            name: 'adjustmentType',
            type: FieldType.SELECT,
            defaultValue: adjustmentType,
            options: typeOptions,
            label: 'Tipo de Ajuste',
            onChange: onChangeAdjustmentType
        },
        {
            name: 'adjustmentYield',
            type: FieldType.PERCENTAGE,
            defaultValue: Math.abs(adjustmentYield),
            label: 'Porcentaje de Ajuste',
            onChange: onChangeYield
        }
    ]
    if (category !== PaymentCategory.CHECK) {
        const financialCostField = {
            name: 'financialCost',
            type: FieldType.PERCENTAGE,
            value: financialCost,
            label: 'Costo Financiero',
            hint: 'Costo financiero + impuestos.',
            onChange: onChangeFinancialCost
        }
        const accreditationField = {
            name: 'accreditation',
            type: FieldType.NUMBER,
            label: 'Plazo de acreditación',
            render: () => (
                <>
                    <BooleanField
                        id="immediateAccreditation"
                        value={immediateAccreditation}
                        label="Inmediato"
                        optional
                        onChange={onChangeImmediateAccreditation}
                    />
                    <NumberField
                        id="accreditationDays"
                        value={accreditationDays}
                        integer
                        disabled={immediateAccreditation}
                        className={styles.accreditationDays}
                        onChange={onChangeAccreditationDays}
                    />
                    <span>días</span>
                </>
            )
        }
        const cashFundsField = {
            name: 'cashFundItems',
            type: FieldType.TABLE,
            table: TableName.CASH_FUNDS,
            label: 'Cajas Asociadas',
            render: () => (
                <Validatable validations={toList(cashFundsValidation)}>
                    <CashFundItemsTable
                        rows={cashFundsData}
                        disableCreate={allCashFunds.length <= cashFundsData.length}
                        onCreate={onAddCashFund}
                        onRemoveRow={onRemoveCashFund}
                    />
                </Validatable>
            )
        }
        fields.push(financialCostField, accreditationField, cashFundsField)
    }

    const roundingEnabledField = {
        name: 'roundingEnabled',
        type: FieldType.BOOLEAN,
        label: 'Habilitar Redondeo',
        render: () => (
            <BooleanField
                id="roundingEnabled"
                value={roundingEnabled}
                label="Permitir redondeo en cobros"
                optional
                onChange={onChangeRoundingEnabled}
            />
        )
    }
    const maxRoundingAmountField = {
        name: 'maxRoundingAmount',
        type: FieldType.PRICE,
        label: 'Máximo de Redondeo',
        render: () => (
            <PriceField
                id="maxRoundingAmount"
                value={maxRoundingAmount}
                min={0.01}
                onChange={(v) => setMaxRoundingAmount(v)}
            />
        )
    }
    fields.push(roundingEnabledField)
    if (roundingEnabled) fields.push(maxRoundingAmountField)

    const validate = (): boolean => {
        if (category !== PaymentCategory.CHECK && cashFundsData.length === 0) {
            setCashFundsValidation('Debe asociar al menos 1 caja')
            return false
        }

        return true
    }

    const onSubmit = (paymentTypeData: Data) => {
        if (validate()) {
            submitPaymentType(
                paymentTypeData,
                adjustmentYield,
                define(financialCost),
                define(accreditationDays),
                cashFundsData,
                roundingEnabled,
                roundingEnabled ? maxRoundingAmount : undefined,
                !enabled,
                createMode
            )
        }
    }

    const onCancel = () => navigate(-1)

    const onSaveCashFund = (newData: Data) => setCashFundsData([newData, ...cashFundsData])

    return (
        <PanelWrapper title={title} crumbs={crumbs}>
            <DataTypeForm
               formId="payment-type-form"
               fields={fields}
               createMode={createMode}
               onSubmit={onSubmit}
               onCancel={onCancel}
            />
            <CashFundItemFormModal
                cashFunds={cashFunds}
                onSubmit={onSaveCashFund}
            />
        </PanelWrapper>
    )
}
